// AGC: Machine Maintenance Service
// AGC: Handles all API calls for machine maintenance records
// AGC: Includes CRUD operations and preventive maintenance configuration

import { getBackendUrl } from '../utils/backendUrl'
import type {
  MachineMaintenanceRecord,
  CreateMaintenanceRecordRequest,
  UpdateMaintenanceRecordRequest,
  PreventiveMaintenanceConfig,
} from '../types/machineMaintenance'

const BASE_URL = getBackendUrl()

/**
 * AGC: Get all maintenance records for a machine
 */
export const getMachineMaintenanceRecords = async (
  machineId: string,
  authToken?: string | null
): Promise<MachineMaintenanceRecord[]> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/machines/${machineId}/maintenance`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch maintenance records: ${response.statusText}`)
    }

    const data = await response.json()
    return data.records || []
  } catch (error) {
    console.error('AGC: Error fetching maintenance records:', error)
    return []
  }
}

/**
 * AGC: Create a new maintenance record
 */
export const createMaintenanceRecord = async (
  machineId: string,
  recordData: CreateMaintenanceRecordRequest,
  authToken?: string | null
): Promise<MachineMaintenanceRecord> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/machines/${machineId}/maintenance`, {
      method: 'POST',
      headers,
      body: JSON.stringify(recordData),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to create maintenance record: ${response.statusText}`)
    }

    const data = await response.json()
    return data.record
  } catch (error) {
    console.error('AGC: Error creating maintenance record:', error)
    throw error
  }
}

/**
 * AGC: Update an existing maintenance record
 */
export const updateMaintenanceRecord = async (
  machineId: string,
  recordId: string,
  recordData: UpdateMaintenanceRecordRequest,
  authToken?: string | null
): Promise<MachineMaintenanceRecord> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/machines/${machineId}/maintenance/${recordId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(recordData),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to update maintenance record: ${response.statusText}`)
    }

    const data = await response.json()
    return data.record
  } catch (error) {
    console.error('AGC: Error updating maintenance record:', error)
    throw error
  }
}

/**
 * AGC: Delete a maintenance record
 */
export const deleteMaintenanceRecord = async (
  machineId: string,
  recordId: string,
  authToken?: string | null
): Promise<void> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/machines/${machineId}/maintenance/${recordId}`, {
      method: 'DELETE',
      headers,
    })

    if (!response.ok) {
      throw new Error(`Failed to delete maintenance record: ${response.statusText}`)
    }
  } catch (error) {
    console.error('AGC: Error deleting maintenance record:', error)
    throw error
  }
}

/**
 * AGC: Update preventive maintenance configuration for a machine
 */
export const updatePreventiveMaintenanceConfig = async (
  machineId: string,
  config: Partial<PreventiveMaintenanceConfig>,
  authToken?: string | null
): Promise<void> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/machines/${machineId}/maintenance-config`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(config),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to update maintenance config: ${response.statusText}`)
    }
  } catch (error) {
    console.error('AGC: Error updating maintenance config:', error)
    throw error
  }
}

/**
 * AGC: Get preventive maintenance configuration for a machine
 */
export const getPreventiveMaintenanceConfig = async (
  machineId: string,
  authToken?: string | null
): Promise<PreventiveMaintenanceConfig | null> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/machines/${machineId}/maintenance-config`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      if (response.status === 404) {
        return null
      }
      throw new Error(`Failed to fetch maintenance config: ${response.statusText}`)
    }

    const data = await response.json()
    return data.config
  } catch (error) {
    console.error('AGC: Error fetching maintenance config:', error)
    return null
  }
}
